import React, { useState, useEffect, memo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { Close, Add, Remove, Star, Warning } from '@mui/icons-material';
import packagesMenuData from '../../data/packagesMenu.json';

// Hoisted static constants
const PACKAGE_LIMITS = {
  starters: 3,
  main: 3,
  rice: 1,
  breads: 2,
  dessert: 1
};

const PACKAGE_PRICING = {
  standard: { price: 499, name: 'Standard Menu Veg (499)' },
  premium: { price: 599, name: 'Premium Menu Veg (599)' }
};

const CUSTOM_PACKAGE_CONFIGS = {
  veg_starters: { name: 'Only Starter Veg', price: 400, limits: { starters: 5 } },
  nonveg_starters: { name: 'Only Starter Non Veg', price: 550, limits: { starters: 6 } },
  veg_main_course: { name: 'Only Main Course Veg', price: 350, limits: { main: 3, rice: 1, breads: 2, dessert: 1 } }
};

const VegPackageModal = ({ 
  open, 
  onClose, 
  packageType: initialPackageType = 'standard', 
  menuItems = [], 
  onAddToCart,
  onRemoveFromCart,
  guestCount = 10,
  preSelectedItem = null,
  starterOnlyPackage = false,
  packageMode = null
}) => {
  const customPackageConfig = CUSTOM_PACKAGE_CONFIGS[packageMode];
  const isStarterOnlyPackage = starterOnlyPackage || packageMode === 'nonveg_starters';
  // Package type state (Standard/Premium)
  const [packageType, setPackageType] = useState(initialPackageType);
  
  // Sync internal packageType state with prop changes
  useEffect(() => {
    if (initialPackageType !== packageType) {
      setPackageType(initialPackageType);
    }
  }, [initialPackageType]);
  // Package limits based on your spreadsheet
  const packageLimits = customPackageConfig?.limits || (starterOnlyPackage ? { starters: 5 } : PACKAGE_LIMITS);

  // Package pricing (per guest)
  const packagePricing = PACKAGE_PRICING;

  // Get menu items based on package type from packagesMenu.json
  const getPackageMenuItems = () => {
    if (customPackageConfig) {
      const categories = { starters: [], main: [], rice: [], breads: [], dessert: [] };
      menuItems.forEach((item) => {
        const category = item.category === 'main_course' ? 'main' : item.category === 'desserts' ? 'dessert' : item.category;
        if (categories[category]) {
          categories[category].push({ ...item, title: item.title || item.name, category, serves: item.serves || guestCount });
        }
      });
      return categories;
    }

    const menuKey = packageType === 'premium' ? 'premiumMenuVeg' : 'standardMenuVeg';
    const packageData = packagesMenuData[menuKey] || {};
    const menuData = packageData.items || {};
    const basePackagePax = Number(packageData.quantityFor?.pax) || 20;
    
    // Transform packagesMenu.json format to component format
    const transformItem = (item, category, index) => ({
      id: `${menuKey}_${category}_${index}`,
      title: item.name,
      name: item.name,
      category: category === 'mainCourse' ? 'main' : category === 'desserts' ? 'dessert' : category,
      quantity: `${item.quantity}${item.unit}`,
      unit: item.unit,
      serves: guestCount,
      basePrice: packageType === 'premium' ? 599 : 499,
      pricePerPortion: `₹${packageType === 'premium' ? 599 : 499}`,
      rating: 4.5,
      image: item.image,
      isVeg: true,
      isNonVeg: false,
      // Quantity for current guest count (scaled from base serves count)
      scaledQuantity: (() => {
        const baseServes = basePackagePax;
        const quantityValue = item.quantity || 0;
        
        if (quantityValue === 0) {
          return `${item.quantity}${item.unit}`;
        }
        
        const scaledValue = (quantityValue / baseServes) * guestCount;
        
        if (item.unit === 'pc' || item.unit === 'pcs') {
          return `${Math.round(scaledValue)}pc`;
        } else if (item.unit === 'kg') {
          return `${Number(scaledValue.toFixed(2))}kg`;
        } else if (item.unit === 'gm') {
          return `${Number(scaledValue.toFixed(1))}gm`;
        } else {
          return `${scaledValue.toFixed(1)}${item.unit}`;
        }
      })()
    });
    
    // Group items by category
    const groupedItems = {};
    
    // Process starters
    if (menuData.starters) {
      groupedItems.starters = menuData.starters.map((item, idx) => transformItem(item, 'starters', idx));
    }
    
    // Process main course
    if (menuData.mainCourse) {
      groupedItems.main = menuData.mainCourse.map((item, idx) => transformItem(item, 'main', idx));
    }
    
    // Process rice
    if (menuData.rice) {
      groupedItems.rice = menuData.rice.map((item, idx) => transformItem(item, 'rice', idx));
    }
    
    // Process breads
    if (menuData.breads) {
      groupedItems.breads = menuData.breads.map((item, idx) => transformItem(item, 'breads', idx));
    }
    
    // Process desserts
    if (menuData.desserts) {
      groupedItems.dessert = menuData.desserts.map((item, idx) => transformItem(item, 'dessert', idx));
    }
    
    return groupedItems;
  };


  // Selected items state with quantities
  const [selectedItems, setSelectedItems] = useState({
    starters: [],
    main: [],
    rice: [],
    breads: [],
    dessert: []
  });

  // Item quantities state for increment/decrement functionality
  const [itemQuantities, setItemQuantities] = useState({});

  useEffect(() => {
    if (packageMode === 'veg_starters' || packageMode === 'nonveg_starters') {
      setItemQuantities({});
    }
  }, [guestCount, packageMode]);

  // Track if we're editing an existing package
  const [isEditing, setIsEditing] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);

  // Serves count - should match guest count
  const [serves, setServes] = useState(guestCount);
  
  // Keep serves synchronized with guestCount
  useEffect(() => {
    setServes(guestCount);
  }, [guestCount]);
  
  // Limit reached popup state
  const [limitReachedOpen, setLimitReachedOpen] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  // Handle pre-selected item when modal opens
  useEffect(() => {
    if (open && preSelectedItem) {
      const cleanItemName = (value) => String(value || '')
        .replace(/\s*\(([^)]*(pc|pcs|kg|gm|g|PCS|KG|GM)[^)]*)\)\s*/gi, ' ')
        .trim()
        .toLowerCase();

      // Determine the category of the pre-selected item
      const itemCategory = preSelectedItem.category;
      let targetCategory = itemCategory;
      
      // Map category names if needed
      if (itemCategory === 'starters') targetCategory = 'starters';
      else if (itemCategory === 'main_course' || itemCategory === 'main') targetCategory = 'main';
      else if (itemCategory === 'breads') targetCategory = 'breads';
      else if (itemCategory === 'desserts' || itemCategory === 'dessert') targetCategory = 'dessert';
      
      // Add a small delay to ensure the modal is fully rendered
      setTimeout(() => {
        setSelectedItems(prev => {
          const packageMenuItems = getPackageMenuItems();
          const targetItems = packageMenuItems[targetCategory] || [];
          const preSelectedName = cleanItemName(preSelectedItem.title || preSelectedItem.name);
          const packageItem = targetItems.find(item => (
            cleanItemName(item.title || item.name) === preSelectedName
          )) || preSelectedItem;
          const isAlreadySelected = prev[targetCategory]?.some(item => item.id === packageItem.id);
          
          if (!isAlreadySelected && targetCategory && packageLimits[targetCategory]) {
            const currentCount = prev[targetCategory]?.length || 0;
            const limit = packageLimits[targetCategory];
            
            
            if (currentCount < limit) {
              return {
                ...prev,
                [targetCategory]: [...(prev[targetCategory] || []), packageItem]
              };
            }
          }
          return prev;
        });
      }, 100);
    }
  }, [open, preSelectedItem, packageType, guestCount]);

  // Handle editing package from cart
  useEffect(() => {
    if (open) {
      try {
        const editingPackageData = sessionStorage.getItem('editingPackage');
        if (editingPackageData) {
          const packageData = JSON.parse(editingPackageData);
          
          // Try to restore selected items from multiple possible sources
          let restoredItems = null;
          
          if (packageData.selectedItems && Object.keys(packageData.selectedItems).length > 0) {
            restoredItems = packageData.selectedItems;
          } else if (packageData.packageDetails) {
            // Convert packageDetails back to selectedItems format
            // We need to find the actual items from the menu data
            const menuItems = getPackageMenuItems();
            restoredItems = {
              starters: [],
              main: [],
              rice: [],
              breads: [],
              dessert: []
            };
            
            // Match items by name
            Object.keys(packageData.packageDetails).forEach(category => {
              if (category === 'extras') return; // Skip extras
              
              const categoryItems = packageData.packageDetails[category] || [];
              const menuCategoryItems = menuItems[category] || [];
              
              categoryItems.forEach(itemName => {
                const foundItem = menuCategoryItems.find(menuItem => {
                  const cleanMenuName = (menuItem.title || menuItem.name || '').replace(/\s*\(([^)]*(pc|pcs|kg|gm|g|PCS|KG|GM)[^)]*)\)\s*/gi, ' ').trim();
                  return cleanMenuName === itemName || (menuItem.title || menuItem.name || '') === itemName;
                });
                
                if (foundItem) {
                  restoredItems[category].push(foundItem);
                } else {
                  
                }
              });
            });
          }
          
          if (restoredItems) {
            // Add a small delay to ensure the modal and menu items are fully rendered
            setTimeout(() => {
              setSelectedItems(restoredItems);
              setIsEditing(true);
              setEditingItemId(packageData.fullItem?.id);
            }, 200);
          } else {
            
          }
          
          // Clear the sessionStorage after using it
          sessionStorage.removeItem('editingPackage');
        }
      } catch (error) {
        console.error('Error restoring package data:', error);
      }
    }
  }, [open]);

  // Reset selected items when modal closes or package type changes
  useEffect(() => {
    if (!open) {
      setSelectedItems({
        starters: [],
        main: [],
        rice: [],
        breads: [],
        dessert: []
      });
      // Reset editing state when modal closes
      setIsEditing(false);
      setEditingItemId(null);
    }
  }, [open]);

  // Reset selections when package type changes (but preserve if editing)
  useEffect(() => {
    // Only reset if not editing (no preSelectedItem) or if modal is not open
    if (!preSelectedItem || !open) {
      setSelectedItems({
        starters: [],
        main: [],
        rice: [],
        breads: [],
        dessert: []
      });
    }
  }, [packageType, preSelectedItem, open]);

  // Get items by category from package menu data
  const itemsByCategory = getPackageMenuItems();

  // Handle item selection with enhanced limit validation and alerts
  const handleItemToggle = (item, category) => {
    const categoryItems = selectedItems[category] || [];
    const isSelected = categoryItems.some(selected => selected.id === item.id);
    const limit = packageLimits[category];
    
    if (isSelected) {
      // Remove item
      setSelectedItems(prev => ({
        ...prev,
        [category]: categoryItems.filter(selected => selected.id !== item.id)
      }));
    } else {
      if (packageMode === 'nonveg_starters' && category === 'starters') {
        const sameDietCount = categoryItems.filter((selected) => item.isNonVeg ? selected.isNonVeg : selected.isVeg).length;
        if (sameDietCount >= 3) {
          setLimitMessage(`Please select exactly 3 veg and 3 non-veg starters. Remove one item before adding "${item.title || item.name}".`);
          setLimitReachedOpen(true);
          return;
        }
      }

      // Check if we can add more items
      if (categoryItems.length >= limit) {
        // Enhanced alert message based on category
        const categoryName = {
          starters: 'Starters',
          main: 'Main Course',
          rice: 'Rice',
          breads: 'Breads',
          dessert: 'Desserts'
        }[category] || category;
        
        const message = `⚠️ ${categoryName} Limit Reached!\n\nYou can select maximum ${limit} ${categoryName.toLowerCase()} items in this package.\n\nPlease remove one item to add "${item.title}".`;
        setLimitMessage(message);
        setLimitReachedOpen(true);
      } else {
        // Add item
        setSelectedItems(prev => ({
          ...prev,
          [category]: [...categoryItems, item]
        }));
      }
    }
  };

  // Check if item is selected
  const isItemSelected = (item, category) => {
    return selectedItems[category]?.some(selected => selected.id === item.id) || false;
  };

  // Get remaining slots for category
  const getRemainingSlots = (category) => {
    const limit = packageLimits[category];
    const selected = selectedItems[category]?.length || 0;
    return limit - selected;
  };

  // Check if category is full
  const isCategoryFull = (category) => {
    return getRemainingSlots(category) === 0;
  };

  const getOriginalQuantityUnit = (item) => {
    const quantitySource = [
      item.portionSize,
      item.portion_size,
      item.scaledQuantity,
      item.calculatedQuantity,
      item.quantity
    ].find((value) => /GM|KG|PCS?|PC/i.test(String(value || '')));

    if (/GM/i.test(String(quantitySource || ''))) return 'GM';
    if (/KG/i.test(String(quantitySource || ''))) return 'KG';
    return 'PCS';
  };

  const getScaledQuantitySource = (item, unit) => [
    item.portion_size,
    item.calculatedQuantity,
    item.scaledQuantity
  ].find((value) => new RegExp(unit, 'i').test(String(value || '')));

  const normalizeQuantityText = (value) => String(value || '')
    .split('+')
    .map((part) => {
      const match = part.trim().match(/^([0-9]+(?:\.[0-9]+)?)\s*(KG|GM|PCS?|PC)(.*)$/i);
      if (!match) return part.trim();
      const amount = Number(match[1]);
      const unit = match[2].toUpperCase();
      const suffix = match[3].trim();
      const displayUnit = unit === 'KG' || unit === 'GM' ? unit : 'pcs';
      return `${amount}${displayUnit}${suffix ? ` ${suffix}` : ''}`;
    })
    .join(' + ');

  // Initialize item quantity
  const getItemQuantity = (item) => {
    const key = `${item.id}_${item.category}`;
    if (itemQuantities[key]) {
      return itemQuantities[key];
    }
    if (packageMode === 'veg_starters' || packageMode === 'nonveg_starters') {
      const unit = getOriginalQuantityUnit(item);
      if (unit === 'GM' || unit === 'KG') {
        const scaledSource = getScaledQuantitySource(item, unit);
        if (scaledSource) {
          return parseFloat(String(scaledSource).match(/[0-9]+(?:\.[0-9]+)?/)?.[0] || '0') || 0;
        }

        const originalSource = item.portionSize || item.quantity;
        const originalAmount = parseFloat(String(originalSource || '').replace(/[^0-9.]/g, '')) || 0;
        const baseServes = Number(item.originalServes || item.serves || guestCount) || guestCount;
        return (originalAmount / baseServes) * guestCount;
      }
      return guestCount * 2;
    }
    // Parse the scaled quantity or fallback to original quantity
    const quantityStr = item.scaledQuantity || item.portion_size || item.calculatedQuantity || item.quantity || '1pc';
    const match = quantityStr.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 1;
  };

  // Update item quantity
  const updateItemQuantity = (item, newQuantity) => {
    const key = `${item.id}_${item.category}`;
    setItemQuantities(prev => ({
      ...prev,
      [key]: Math.max(1, newQuantity)
    }));
  };

  // Handle quantity increment
  const handleQuantityIncrement = (item) => {
    const currentQty = getItemQuantity(item);
    updateItemQuantity(item, currentQty + 1);
  };

  // Handle quantity decrement
  const handleQuantityDecrement = (item) => {
    const currentQty = getItemQuantity(item);
    if (currentQty > 1) {
      updateItemQuantity(item, currentQty - 1);
    }
  };

  // Get display quantity for item
  const getDisplayQuantity = (item) => {
    if (packageMode === 'veg_starters' || packageMode === 'nonveg_starters') {
      const unit = getOriginalQuantityUnit(item);
      const key = `${item.id}_${item.category}`;
      const scaledSource = getScaledQuantitySource(item, unit);
      if (!itemQuantities[key] && String(scaledSource || '').includes('+')) {
        return normalizeQuantityText(scaledSource);
      }

      const quantity = getItemQuantity(item);
      if (unit === 'GM') return `${Number(quantity.toFixed(1))}GM`;
      if (unit === 'KG') return `${Number(quantity.toFixed(2))}KG`;
      return `${Math.round(quantity)}pcs`;
    }
    const quantity = getItemQuantity(item);
    const originalQuantity = item.scaledQuantity || item.portion_size || item.calculatedQuantity || item.quantity || '1pc';
    
    // Determine unit from original quantity
    if (/kg/i.test(originalQuantity)) {
      return `${Number(quantity.toFixed(2))}kg`;
    } else if (/gm/i.test(originalQuantity)) {
      return `${Number(quantity.toFixed(1))}gm`;
    } else if (/pc/i.test(originalQuantity)) {
      return `${Math.round(quantity)}pc`;
    } else {
      return `${Math.round(quantity)}pc`;
    }
  };

  // Get total selected items count
  const getTotalSelectedCount = () => {
    return Object.values(selectedItems).reduce((total, items) => total + items.length, 0);
  };

  // Compute pricing for current selection (₹499 per guest)
  const getPackagePricing = () => {
    const pricePerGuest = customPackageConfig?.price || (starterOnlyPackage ? 400 : packagePricing[packageType].price);
    const packagePrice = pricePerGuest * guestCount;
    return { pricePerGuest, packagePrice };
  };

  // Handle add to cart
  const handleAddPackageToCart = () => {
    // Calculate total selected items
    const totalSelected = Object.values(selectedItems).reduce((total, items) => total + items.length, 0);

    // Pricing: ₹499 per guest (both Standard and Premium)
    // Use per-guest price as unit price; let cart multiply by quantity (guestCount)
    const { pricePerGuest, packagePrice } = getPackagePricing();
    const quantity = guestCount;
    
    const cleanName = (item) => {
      const raw = item.title || item.name || '';
      // Remove patterns like "(40pc)", "(0.7kg)", "(14 PCS)", "(0.7 KG)" etc.
      return raw.replace(/\s*\(([^)]*(pc|pcs|kg|gm|g|PCS|KG|GM)[^)]*)\)\s*/gi, ' ').trim();
    };
    const itemWithQuantity = (item) => `${cleanName(item)} - ${getDisplayQuantity(item)}`;

    const packageItem = {
      id: isEditing ? editingItemId : `veg_package_${Date.now()}`,
      name: customPackageConfig?.name || (starterOnlyPackage ? 'Only Starter Veg' : packagePricing[packageType].name),
      // Use per-guest pricing so cart quantity reflects guest count
      price: pricePerGuest,
      // Do not store total in calculatedPrice to avoid double-multiplying in CartContext
      calculatedPrice: pricePerGuest,
      quantity,
      serves: serves,
      guestCount: guestCount,
      isPackage: true,
      isVeg: true, // Mark as vegetarian package
      isNonVeg: false, // Explicitly mark as not non-vegetarian
      packageType: packageType,
      starterOnlyPackage: isStarterOnlyPackage,
      packageMode,
      selectedItems: selectedItems,
      packageItems: packagesMenuData[packageType === 'premium' ? 'premiumMenuVeg' : 'standardMenuVeg']?.items,
      packagePax: packagesMenuData[packageType === 'premium' ? 'premiumMenuVeg' : 'standardMenuVeg']?.quantityFor?.pax || 20,
      packageDetails: {
        // Only store clean item names here; strip quantity markers like (40pc), (0.7kg), etc.
        starters: selectedItems.starters?.map(cleanName) || [],
        main: selectedItems.main?.map(cleanName) || [],
        rice: selectedItems.rice?.map(cleanName) || [],
        breads: selectedItems.breads?.map(cleanName) || [],
        dessert: selectedItems.dessert?.map(cleanName) || [],
        extras: packageMode === 'veg_main_course' ? ['Raita & Salad'] : isStarterOnlyPackage ? [] : ['Raita & Salad']
      },
      packageQuantityDetails: {
        starters: selectedItems.starters?.map(itemWithQuantity) || [],
        main: selectedItems.main?.map(itemWithQuantity) || [],
        rice: selectedItems.rice?.map(itemWithQuantity) || [],
        breads: selectedItems.breads?.map(itemWithQuantity) || [],
        dessert: selectedItems.dessert?.map(itemWithQuantity) || [],
        extras: packageMode === 'veg_main_course' ? ['Raita & Salad'] : isStarterOnlyPackage ? [] : ['Raita & Salad']
      },
      totalSelectedItems: totalSelected,
      priceBreakdown: {
        packagePrice: packagePrice,
        guestCount: guestCount,
        pricePerGuest: pricePerGuest
      },
      // Add editing metadata
      isEditing: isEditing,
      originalId: isEditing ? editingItemId : null
    };

    // If editing, remove the old item first
    if (isEditing && editingItemId && onRemoveFromCart) {
      onRemoveFromCart(editingItemId);
    }
    
    onAddToCart(packageItem);
    onClose();
  };

  // Check if package is complete (all required categories have selections)
  const isPackageComplete = () => {
    if (packageMode === 'nonveg_starters') {
      const starters = selectedItems.starters || [];
      return starters.length === 6 && starters.filter((item) => item.isVeg).length === 3 && starters.filter((item) => item.isNonVeg).length === 3;
    }
    if (starterOnlyPackage) return (selectedItems.starters || []).length === 5;
    if (packageMode === 'veg_main_course') {
      return Object.entries(packageLimits).every(([category, limit]) => (selectedItems[category] || []).length === limit);
    }

    return Object.keys(packageLimits).every(category => {
      const selected = selectedItems[category]?.length || 0;
      return selected > 0; // At least one item selected in each category
    });
  };

  // Reset selections when modal opens (but not if there's a preSelectedItem)
  useEffect(() => {
    if (open && !preSelectedItem) {
      setSelectedItems({
        starters: [],
        main: [],
        rice: [],
        breads: [],
        dessert: []
      });
    }
  }, [open, preSelectedItem]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          width: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        pb: 2,
        bgcolor: '#f8f9fa'
      }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1.1rem' }}>
              {customPackageConfig?.name || (starterOnlyPackage ? 'Only Starter Veg' : packagePricing[packageType].name)}
            </Typography>
            {!isStarterOnlyPackage && <Chip
              label={packageType === 'premium' ? 'Premium' : 'Standard'} 
              color={packageType === 'premium' ? 'warning' : 'primary'}
              size="small"
              sx={{ fontWeight: 'bold' }}
            />}
          </Box>
          
          {/* Package Type Selector */}
          <ToggleButtonGroup
            value={packageType}
            exclusive
            onChange={(e, newPackageType) => {
              if (newPackageType !== null) {
                setPackageType(newPackageType);
              }
            }}
            size="small"
            sx={{ mb: 2, display: customPackageConfig ? 'none' : 'flex' }}
          >
            <ToggleButton value="standard" sx={{ px: 2, py: 0.5, fontSize: '0.8rem' }}>
              Standard Menu
            </ToggleButton>
            <ToggleButton value="premium" sx={{ px: 2, py: 0.5, fontSize: '0.8rem' }}>
              Premium Menu
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Box sx={{ display: customPackageConfig ? 'none' : 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">• 3 Starters</Typography>
            <Typography variant="body2" color="text.secondary">• 3 Main Course</Typography>
            <Typography variant="body2" color="text.secondary">• 1 Rice</Typography>
            <Typography variant="body2" color="text.secondary">• 2 Breads</Typography>
            <Typography variant="body2" color="text.secondary">• 1 Desserts</Typography>
            <Typography variant="body2" color="text.secondary">• Raita & Salad</Typography>
          </Box>
          {starterOnlyPackage && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select any 5 Veg Starters - ₹400 per person
            </Typography>
          )}
          
          {packageMode === 'nonveg_starters' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select exactly 3 veg and 3 non-veg starters - ₹550 per person.
            </Typography>
          )}
          {packageMode === 'veg_main_course' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select 3 Main Course, 1 Rice, 2 Breads and 1 Dessert. Raita and Salad included - ₹350 per person.
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
            Quantity for {guestCount} guests • Prices calculated per guest
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Customize Your Meal Header */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
          Customize Your Meal
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic', display: customPackageConfig ? 'none' : 'block' }}>
          Select your preferred items - Package price remains ₹{packagePricing[packageType].price}
        </Typography>
        {starterOnlyPackage && (
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
            Select 5 veg starters. Package price is ₹400 per person.
          </Typography>
        )}

        {packageMode === 'nonveg_starters' && (
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
            Select exactly 3 veg and 3 non-veg starters. Package price is ₹550 per person.
          </Typography>
        )}
        {packageMode === 'veg_main_course' && (
          <Typography variant="body2" sx={{ color: '#666', mb: 2, fontStyle: 'italic' }}>
            Select 3 main course, 1 rice, 2 breads and 1 dessert. Package price is ₹350 per person.
          </Typography>
        )}

        {/* Starters Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
            Starters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select any {packageMode === 'nonveg_starters' ? 6 : starterOnlyPackage ? 5 : 3} items
          </Typography>
          
          {(itemsByCategory.starters || []).map((item) => {
            const isSelected = isItemSelected(item, 'starters');
            const isDisabled = !isSelected && isCategoryFull('starters');
            
            return (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                py: 1, 
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: isDisabled ? '#f8f8f8' : 'transparent',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: isDisabled ? '#f0f0f0' : '#f5f5f5'
                }
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      disabled={false} // Never disable checkbox, handle click logic instead
                      onChange={() => handleItemToggle(item, 'starters')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: '500' }}>{item.title || item.name}</span>
                      </Box>
                      
                    </Box>
                  }
                  sx={{ 
                    flex: 1,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      color: isDisabled ? '#666' : 'inherit'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  border: '1px solid #1976d2',
                  borderRadius: 1,
                  bgcolor: 'white',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      borderRadius: 0,
                      minWidth: '32px',
                      height: '32px',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                    onClick={() => handleQuantityDecrement(item)}
                  >
                    <Remove sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Box sx={{ 
                    px: 1, 
                    py: 1, 
                    minWidth: '35px', 
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    {getDisplayQuantity(item)}
                  </Box>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      borderRadius: 0,
                      minWidth: '32px',
                      height: '32px',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                    onClick={() => handleQuantityIncrement(item)}
                  >
                    <Add sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Main Course Section */}
        <Box sx={{ mb: 3, display: isStarterOnlyPackage ? 'none' : 'block' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
            Main Course
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select any 3 items
          </Typography>
          
          {(itemsByCategory.main || []).map((item) => {
            const isSelected = isItemSelected(item, 'main');
            const isDisabled = !isSelected && isCategoryFull('main');
            
            return (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                py: 1, 
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: isDisabled ? '#f8f8f8' : 'transparent', // Light gray background for disabled items
                cursor: 'pointer', 
                '&:hover': {
                  backgroundColor: isDisabled ? '#f0f0f0' : '#f5f5f5' // Hover effect
                }
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      disabled={false} // Never disable checkbox, handle click logic instead
                      onChange={() => handleItemToggle(item, 'main')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: '500' }}>{item.title || item.name}</span>
                      </Box>
                    </Box>
                  }
                  sx={{ 
                    flex: 1,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      color: isDisabled ? '#999' : 'inherit'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  border: '1px solid #1976d2',
                  borderRadius: 1,
                  bgcolor: 'white',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      borderRadius: 0,
                      minWidth: '32px',
                      height: '32px',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                    onClick={() => handleQuantityDecrement(item)}
                  >
                    <Remove sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Box sx={{ 
                    px: 1, 
                    py: 1, 
                    minWidth: '35px', 
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    {getDisplayQuantity(item)}
                  </Box>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      borderRadius: 0,
                      minWidth: '32px',
                      height: '32px',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                    onClick={() => handleQuantityIncrement(item)}
                  >
                    <Add sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Rice Section */}
        <Box sx={{ mb: 3, display: isStarterOnlyPackage ? 'none' : 'block' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
            Rice
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select any 1 item
          </Typography>
          
          {(itemsByCategory.rice || []).map((item) => {
            const isSelected = isItemSelected(item, 'rice');
            const isDisabled = !isSelected && isCategoryFull('rice');
            
            return (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                py: 1, 
                borderBottom: '1px solid #f0f0f0',
                opacity: isDisabled ? 0.6 : 1
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      disabled={false}
                      onChange={() => handleItemToggle(item, 'rice')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: '500' }}>{item.title || item.name}</span>
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 12, color: '#ffa726' }} />
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                            {item.rating?.toFixed(1) || '4.5'}
                          </Typography>
                        </Box> */}
                      </Box>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • {item.scaledQuantity || item.quantity}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • For {guestCount} guests
                        </Typography>
                      </Box> */}
                    </Box>
                  }
                  sx={{ 
                    flex: 1,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      color: isDisabled ? '#999' : 'inherit'
                    }
                  }}
                />
                <TextField
                  size="small"
                  value={item.scaledQuantity || item.quantity}
                  disabled
                  sx={{ 
                    width: '80px',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Breads Section */}
        <Box sx={{ mb: 3, display: isStarterOnlyPackage ? 'none' : 'block' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
            Breads
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select any 2 items
          </Typography>
          
          {(itemsByCategory.breads || []).map((item) => {
            const isSelected = isItemSelected(item, 'breads');
            const isDisabled = !isSelected && isCategoryFull('breads');
            
            return (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                py: 1, 
                borderBottom: '1px solid #f0f0f0',
                opacity: isDisabled ? 0.6 : 1 // Show disabled items with reduced opacity
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      disabled={false} // Never disable checkbox, handle click logic instead
                      onChange={() => handleItemToggle(item, 'breads')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: '500' }}>{item.title || item.name}</span>
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 12, color: '#ffa726' }} />
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                            {item.rating?.toFixed(1) || '4.2'}
                          </Typography>
                        </Box> */}
                      </Box>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • {item.scaledQuantity || item.quantity}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • For {guestCount} guests
                        </Typography>
                      </Box> */}
                    </Box>
                  }
                  sx={{ 
                    flex: 1,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      color: isDisabled ? '#999' : 'inherit'
                    }
                  }}
                />
                <TextField
                  size="small"
                  value={item.scaledQuantity || item.quantity}
                  disabled
                  sx={{ 
                    width: '80px',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>

        {/* Desserts Section */}
        <Box sx={{ mb: 3, display: isStarterOnlyPackage ? 'none' : 'block' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#333' }}>
            Desserts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select any 1 item
          </Typography>
          
          {(itemsByCategory.dessert || []).map((item) => {
            const isSelected = isItemSelected(item, 'dessert');
            const isDisabled = !isSelected && isCategoryFull('dessert');
            
            return (
              <Box key={item.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                py: 1, 
                borderBottom: '1px solid #f0f0f0',
                opacity: isDisabled ? 0.6 : 1 // Show disabled items with reduced opacity
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isSelected}
                      disabled={false} // Never disable checkbox, handle click logic instead
                      onChange={() => handleItemToggle(item, 'dessert')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontWeight: '500' }}>{item.title || item.name}</span>
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 12, color: '#ffa726' }} />
                          <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                            {item.rating?.toFixed(1) || '4.4'}
                          </Typography>
                        </Box> */}
                      </Box>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • {item.scaledQuantity || item.quantity}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem' }}>
                          • For {guestCount} guests
                        </Typography>
                      </Box> */}
                    </Box>
                  }
                  sx={{ 
                    flex: 1,
                    '& .MuiFormControlLabel-label': { 
                      fontSize: '0.9rem',
                      color: isDisabled ? '#999' : 'inherit'
                    }
                  }}
                />
                <TextField
                  size="small"
                  value={item.scaledQuantity || item.quantity}
                  disabled
                  sx={{ 
                    width: '80px',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '0.8rem'
                    }
                  }}
                />
              </Box>
            );
          })}
        </Box>


        {/* Serves Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
            Serves
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setServes(Math.max(1, serves - 1))}>
              <Remove sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: '40px', textAlign: 'center', fontWeight: 'bold' }}>
              {serves}
            </Typography>
            <IconButton size="small" onClick={() => setServes(serves + 1)}>
              <Add sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Add Button */}
        {/* Package Summary */}
        {(() => {
          const { pricePerGuest, packagePrice } = getPackagePricing();
          return (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Package Summary
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block', fontStyle: 'italic' }}>
            ₹{pricePerGuest} per guest • Total price updates as you change guest count
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Selected Items:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {getTotalSelectedCount()} / {Object.values(packageLimits).reduce((a, b) => a + b, 0)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Guest Count:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{guestCount} guests</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Package Price:</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              ₹{packagePrice}
            </Typography>
          </Box>
        </Box>
          );
        })()}

        <Button
          fullWidth
          variant="contained"
          onClick={handleAddPackageToCart}
          disabled={!isPackageComplete()}
          sx={{
            bgcolor: isPackageComplete() ? '#1976d2' : '#ccc',
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 'bold',
            borderRadius: 1,
            '&:hover': {
              bgcolor: isPackageComplete() ? '#1565c0' : '#ccc'
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666'
            }
          }}
        >
          {(() => {
            const { packagePrice } = getPackagePricing();
            return isPackageComplete() 
              ? `Add Package | ₹${packagePrice}` 
              : 'Please complete your selection';
          })()}
        </Button>
      </DialogContent>

      {/* Limit Reached Popup */}
      <Dialog
        open={limitReachedOpen}
        onClose={() => {
          setLimitReachedOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: 99999, // Even higher z-index
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)' // Even darker backdrop
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            textAlign: 'center',
            p: 3,
            backgroundColor: 'white',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            minWidth: '320px',
            border: '2px solid red', // Temporary red border for visibility
            position: 'relative',
            zIndex: 10000
          }
        }}
      >
        <DialogContent sx={{ pt: 2, pb: 1, px: 3, textAlign: 'center' }}>
          <Box sx={{ 
            width: 60, 
            height: 60, 
            borderRadius: '50%', 
            bgcolor: '#ff5722', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}>
            <Warning sx={{ color: 'white', fontSize: 30 }} />
          </Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            mb: 2, 
            color: '#d32f2f'
          }}>
            Selection Limit Reached!
          </Typography>
          <Typography variant="body1" sx={{ 
            fontWeight: '400', 
            mb: 2, 
            color: '#333',
            fontSize: '14px',
            lineHeight: 1.5,
            whiteSpace: 'pre-line'
          }}>
            {limitMessage}
          </Typography>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="contained"
            onClick={() => setLimitReachedOpen(false)}
            sx={{
              bgcolor: '#1976d2',
              px: 6,
              py: 1.2,
              borderRadius: 2,
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'none',
              minWidth: '100px',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            Okay
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default VegPackageModal;
